import { GridApi } from "ag-grid-community";
import { useCallback, useReducer } from "react";

type WellsViewStateType = {
    // Options state
    configurationID: number;
    filterText: string;

    gridApi: GridApi | undefined;

    // Other state
    defaultConfigurationID: number;
    isNoData: boolean;
    isOptionsDirty: boolean;

    downloadUid: string | undefined;

    refreshing: boolean;

    summaryListingDisplayFunctionIcons: boolean;
    summaryListingAlarmAtTop: boolean;

    initialSummaryListingDisplayFunctionIcons?: boolean;
    initialSummaryListingAlarmAtTop?: boolean;


};

const initialState: WellsViewStateType = {
    configurationID: -1,
    defaultConfigurationID: -1,
    filterText: "",
    isNoData: false,
    isOptionsDirty: false,
    gridApi: undefined,
    downloadUid: undefined,
    refreshing: false,

    summaryListingDisplayFunctionIcons: false,
    summaryListingAlarmAtTop: false,
};

enum WellsViewActionEnum {
    ResetOptionsRequested = "RESET_OPTIONS_REQUESTED",
    ConfigurationChanged = "CONFIGURATION_CHANGED",
    DefaultConfigurationChanged = "DEFAULT_CONFIGURATION_CHANGED",
    FilterTextChanged = "FILTER_TEXT_CHANGED",
    NoDataChanged = "NO_DATA_CHANGED",
    GridApiChanged = 'GRID_API_CHANGED',
    DownloadUidChanged = 'WELL_DOWNLOAD_UID_CHANGED',
    RefreshingChanged = 'WELL_REFRESHING_CHANGED',
    DisplayFunctionIconsChanged = "WELL_VIEW_DISPLAY_FUNCTION_ICONS",
    DisplayAlarmsAtTopChanged = "WELL_VIEW_ALARMS_AT_TOP",

}

type WellsViewActionTypes = {
    type: WellsViewActionEnum.ResetOptionsRequested;
} | {
    type: WellsViewActionEnum.ConfigurationChanged;
    configurationIDOrCallback: number | ((configurationID: number) => number);
} | {
    type: WellsViewActionEnum.DefaultConfigurationChanged;
    defaultConfigurationID: number;
} | {
    type: WellsViewActionEnum.FilterTextChanged;
    filterText: string;
} | {
    type: WellsViewActionEnum.GridApiChanged;
    payload: GridApi;
} | {
    type: WellsViewActionEnum.DownloadUidChanged;
    payload: string | undefined;
} | {
    type: WellsViewActionEnum.NoDataChanged;
    isNoData: boolean;
} | {
    type: WellsViewActionEnum.DisplayFunctionIconsChanged;
    payload: boolean;
} | {
    type: WellsViewActionEnum.DisplayAlarmsAtTopChanged;
    payload: boolean;
} | {
    type: WellsViewActionEnum.RefreshingChanged;
    payload: boolean;
};

function reducer(state: WellsViewStateType, action: WellsViewActionTypes): WellsViewStateType {

    const copy = { ...state };
    switch (action.type) {

        case WellsViewActionEnum.ResetOptionsRequested:
            copy.configurationID = state.defaultConfigurationID;
            copy.filterText = initialState.filterText;
            copy.isOptionsDirty = false;
            return copy;

        case WellsViewActionEnum.ConfigurationChanged:
            copy.configurationID = typeof action.configurationIDOrCallback === "function"
                ? action.configurationIDOrCallback(state.configurationID)
                : action.configurationIDOrCallback
            copy.isOptionsDirty = isOptionsDirty(copy);
            return copy;

        case WellsViewActionEnum.FilterTextChanged:
            copy.filterText = action.filterText;
            copy.isOptionsDirty = isOptionsDirty(copy);
            return copy;

        case WellsViewActionEnum.DefaultConfigurationChanged:
            copy.defaultConfigurationID = action.defaultConfigurationID;
            return copy;

        case WellsViewActionEnum.NoDataChanged:
            copy.isNoData = action.isNoData;
            return copy;

        case WellsViewActionEnum.GridApiChanged:
            copy.gridApi = action.payload;
            return copy;

        case WellsViewActionEnum.DownloadUidChanged:
            copy.downloadUid = action.payload;
            return copy;

        case WellsViewActionEnum.RefreshingChanged:
            copy.refreshing = action.payload;
            return copy;

        case WellsViewActionEnum.DisplayFunctionIconsChanged:
            copy.summaryListingDisplayFunctionIcons = action.payload;
            if (copy.initialSummaryListingDisplayFunctionIcons === undefined) {
                copy.initialSummaryListingDisplayFunctionIcons = action.payload;
            }
            copy.isOptionsDirty = isOptionsDirty(copy);
            return copy;

        case WellsViewActionEnum.DisplayAlarmsAtTopChanged:
            copy.summaryListingAlarmAtTop = action.payload;
            if (copy.initialSummaryListingAlarmAtTop === undefined) {
                copy.initialSummaryListingAlarmAtTop = action.payload;
            }
            copy.isOptionsDirty = isOptionsDirty(copy);
            return copy;

                
        default:
            return state;
    }
}

function isOptionsDirty(state: WellsViewStateType) {
    return state.configurationID !== state.defaultConfigurationID ||
        state.filterText !== initialState.filterText ||
        (state.initialSummaryListingDisplayFunctionIcons !== undefined && state.initialSummaryListingDisplayFunctionIcons !== state.summaryListingDisplayFunctionIcons) ||
        (state.initialSummaryListingAlarmAtTop !== undefined && state.initialSummaryListingAlarmAtTop !== state.summaryListingAlarmAtTop);
}

type UseWellsViewStateType = {
    state: WellsViewStateType;
    resetOptions: () => void;
    setConfigurationID: (configurationIDOrCallback: number | ((configurationID: number) => number)) => void;
    setFilterText: (filterText: string) => void;
    setDefaultConfigurationID: (defaultConfigurationID: number) => void;
    setNoData: (isNoData: boolean) => void;
    setGridApi: (value: GridApi) => void;
    setDownloadUid: (value: string | undefined) => void;
    setRefreshing: (value: boolean) => void;
    setFunctionIcons: (value: boolean) => void;
    setAlarmsAtTop: (value: boolean) => void;

};

export default function useWellsViewState(): UseWellsViewStateType {
    const [state, dispatch] = useReducer(reducer, initialState);

    const resetOptions = useCallback(() => dispatch({
            type: WellsViewActionEnum.ResetOptionsRequested
        }), []);

    const setConfigurationID = useCallback((configurationIDOrCallback: number | ((configurationID: number) => number)) => dispatch({
            type: WellsViewActionEnum.ConfigurationChanged,
            configurationIDOrCallback: configurationIDOrCallback
    }), []);

    const setFilterText = useCallback((filterText: string) => dispatch({
            type: WellsViewActionEnum.FilterTextChanged,
            filterText: filterText
        }), []);

    const setDefaultConfigurationID = useCallback((configurationID: number) => dispatch({
            type: WellsViewActionEnum.DefaultConfigurationChanged,
            defaultConfigurationID: configurationID
        }), []);

    const setNoData = useCallback((isNoData: boolean) => dispatch({
            type: WellsViewActionEnum.NoDataChanged,
            isNoData: isNoData
        }), []);

    const setGridApi = useCallback((value: GridApi) => dispatch({
        type: WellsViewActionEnum.GridApiChanged,
        payload: value
    }), []);

    const setDownloadUid = useCallback((value: string | undefined) => dispatch({
        type: WellsViewActionEnum.DownloadUidChanged,
        payload: value
    }), []);

    const setRefreshing = useCallback((value: boolean) => dispatch({
        type: WellsViewActionEnum.RefreshingChanged,
        payload: value
    }), []);

    const setFunctionIcons = useCallback((value: boolean) => dispatch({
        type: WellsViewActionEnum.DisplayFunctionIconsChanged,
        payload: value
    }), []);


    const setAlarmsAtTop = useCallback((value: boolean) => dispatch({
        type: WellsViewActionEnum.DisplayAlarmsAtTopChanged,
        payload: value
    }), []);


    return {
        state,
        resetOptions,
        setConfigurationID,
        setFilterText,
        setDefaultConfigurationID,
        setNoData,
        setGridApi,
        setDownloadUid,
        setRefreshing,
        setFunctionIcons,
        setAlarmsAtTop,

    };
}    
